import React, { useEffect, useRef, useState } from 'react';
import api from '../../utils/api';
import {
  Settings, Save, Plus, Trash2, School, Sliders, Truck, GitBranch,
  Search, ChevronDown, ChevronRight, Edit2, Copy, RotateCcw, Download,
  Layers, Globe, BookOpen, Users, DollarSign, GraduationCap, CheckCircle,
  Upload, ImageIcon, Loader2, Link, KeyRound, GripVertical, Columns,
} from 'lucide-react';
import { toast } from 'sonner';
import RightSlideModal from '../../components/RightSlideModal';
import DiscountWorkflowPage from './DiscountWorkflowPage';

export default function SettingsPage() {
  const [activeTab, setActiveTab] = useState('general'); // 'general', 'components', 'routes', 'discounts', 'workflows'
  const [school, setSchool] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newSession, setNewSession] = useState('');
  const [savingSchool, setSavingSchool] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const logoFileRef = useRef(null);

  // Configurations states
  const [feeComponents, setFeeComponents] = useState([]);
  const [transportRoutes, setTransportRoutes] = useState([]);
  const [discountRules, setDiscountRules] = useState([]);
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);

  // Student field settings
  const [fieldSettings, setFieldSettings] = useState({
    rollNumber: false, admissionNumber: true, dateOfBirth: false,
    fatherName: true, fatherPhone: true, motherName: false, motherPhone: false,
    aadharNumber: false, address: false, familyId: false,
  });
  const [savingFields, setSavingFields] = useState(false);

  // Student portal settings
  const [portalSettings, setPortalSettings] = useState({ createCredentials: true });
  const [savingPortal, setSavingPortal] = useState(false);

  // Student column settings
  const DEFAULT_COLUMN_SETTINGS = [
    { key: 'student',      visible: true  },
    { key: 'admissionNo',  visible: false },
    { key: 'rollNo',       visible: false },
    { key: 'classSec',     visible: true  },
    { key: 'dateOfBirth',  visible: false },
    { key: 'fatherName',   visible: false },
    { key: 'fatherPhone',  visible: false },
    { key: 'motherName',   visible: false },
    { key: 'motherPhone',  visible: false },
    { key: 'aadharNumber', visible: false },
    { key: 'address',      visible: false },
    { key: 'familyId',     visible: false },
    { key: 'transport',    visible: false },
    { key: 'credentials',  visible: false },
  ];
  const COLUMN_META = {
    student:      { label: 'Student Name',     alwaysVisible: true  },
    admissionNo:  { label: 'Admission No.',    alwaysVisible: false },
    rollNo:       { label: 'Roll No.',         alwaysVisible: false },
    classSec:     { label: 'Class & Section',  alwaysVisible: true  },
    dateOfBirth:  { label: 'Date of Birth',    alwaysVisible: false },
    fatherName:   { label: 'Father Name',      alwaysVisible: false },
    fatherPhone:  { label: 'Father Phone',     alwaysVisible: false },
    motherName:   { label: 'Mother Name',      alwaysVisible: false },
    motherPhone:  { label: 'Mother Phone',     alwaysVisible: false },
    aadharNumber: { label: 'Aadhar Number',    alwaysVisible: false },
    address:      { label: 'Address',          alwaysVisible: false },
    familyId:     { label: 'Family ID',        alwaysVisible: false },
    transport:    { label: 'Transport',        alwaysVisible: false },
    credentials:  { label: 'Login Credentials',alwaysVisible: false },
  };
  const [columnSettings, setColumnSettings] = useState(DEFAULT_COLUMN_SETTINGS);
  const [savingColumns, setSavingColumns] = useState(false);
  const [colDragIdx, setColDragIdx] = useState(null);
  const [colDragOverIdx, setColDragOverIdx] = useState(null);

  // Fee Components tab state
  const [selectedComp, setSelectedComp] = useState(null);
  const [compSubTab, setCompSubTab] = useState('all');
  const [compSearch, setCompSearch] = useState('');
  const [compFreqFilter, setCompFreqFilter] = useState('all');
  const [compScopeFilter, setCompScopeFilter] = useState('all');
  const [compClassFilter, setCompClassFilter] = useState('all');
  const [treeExpanded, setTreeExpanded] = useState({ global: true, classwise: true });
  const [expandedClasses, setExpandedClasses] = useState(new Set());
  const [editingComp, setEditingComp] = useState(null);

  // Modal control states
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [configModalType, setConfigModalType] = useState('component'); // 'component', 'transport', 'discount', 'edit-component'

  // Forms
  const [compForm, setCompForm] = useState({ name: '', amount: '', frequency: 'monthly', type: 'mandatory', classId: 'all' });
  const [routeForm, setRouteForm] = useState({ name: '', routeCode: '', monthlyFee: '', pickupPointInput: '' });
  const [discountForm, setDiscountForm] = useState({ name: '', target: 'tuition', value: '', valueType: 'percentage', condition: '' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [schoolRes, sessionsRes, compsRes, routesRes, discountsRes, classesRes, studentsRes, fieldSettingsRes, portalSettingsRes, columnSettingsRes] = await Promise.all([
        api.get('/auth/me'),
        api.get('/academic-sessions'),
        api.get('/fee-components'),
        api.get('/transport-routes'),
        api.get('/discount-rules'),
        api.get('/classes').catch(() => ({ data: [] })),
        api.get('/students').catch(() => ({ data: [] })),
        api.get('/student-field-settings').catch(() => ({ data: null })),
        api.get('/student-portal-settings').catch(() => ({ data: null })),
        api.get('/student-column-settings').catch(() => ({ data: null })),
      ]);
      const user = schoolRes.data;
      const schoolsRes = await api.get('/schools');
      const currentSchool = schoolsRes.data.find(s => s.id === user.schoolId);
      setSchool(currentSchool);
      setSessions(sessionsRes.data);
      setFeeComponents(compsRes.data || []);
      setTransportRoutes(routesRes.data || []);
      setDiscountRules(discountsRes.data || []);
      setClasses(classesRes.data || []);
      setStudents(studentsRes.data || []);
      if (fieldSettingsRes.data) setFieldSettings(fieldSettingsRes.data);
      if (portalSettingsRes.data) setPortalSettings(portalSettingsRes.data);
      if (columnSettingsRes.data) {
        const saved = columnSettingsRes.data;
        const validKeys = new Set(DEFAULT_COLUMN_SETTINGS.map(d => d.key));
        const filtered = saved.filter(c => validKeys.has(c.key));
        const filteredKeys = new Set(filtered.map(c => c.key));
        const appended = DEFAULT_COLUMN_SETTINGS.filter(d => !filteredKeys.has(d.key));
        setColumnSettings([...filtered, ...appended]);
      }
    } catch (error) {
      toast.error('Failed to load settings configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSchoolUpdate = async (e) => {
    e.preventDefault();
    setSavingSchool(true);
    try {
      await api.put(`/schools/${school.id}`, {
        name: school.name,
        schoolCode: school.schoolCode,
        address: school.address,
        adminContact: school.adminContact,
        logoUrl: school.logoUrl || '',
        logoPlacement: school.logoPlacement || 'center'
      });
      toast.success('School profile updated successfully');
    } catch (error) {
      toast.error('Failed to update school details');
    } finally {
      setSavingSchool(false);
    }
  };

  const handleAddSession = async (e) => {
    e.preventDefault();
    if (!newSession.trim()) return;
    try {
      const res = await api.post('/academic-sessions', { sessionYear: newSession.trim() });
      setSessions([...sessions, res.data]);
      setNewSession('');
      toast.success('Academic session added successfully');
    } catch (error) {
      toast.error('Failed to add session');
    }
  };

  const handleDeleteSession = async (id) => {
    if (!window.confirm('Delete this session? Existing data won\'t be deleted but it won\'t show in filters.')) return;
    try {
      await api.delete(`/academic-sessions/${id}`);
      setSessions(sessions.filter(s => s.id !== id));
      toast.success('Session deleted');
    } catch (error) {
      toast.error('Failed to delete session');
    }
  };

  // Configurations API handlings
  const handleDeleteComponent = async (compId) => {
    if (!window.confirm('Are you sure you want to delete this fee component?')) return;
    try {
      const updated = feeComponents.filter(c => c.id !== compId);
      await api.post('/fee-components', updated);
      setFeeComponents(updated);
      toast.success('Component deleted successfully');
    } catch (err) {
      toast.error('Failed to delete component from MongoDB');
    }
  };

  const handleDeleteRoute = async (routeId) => {
    if (!window.confirm('Are you sure you want to delete this transport route?')) return;
    try {
      const updated = transportRoutes.filter(r => r.id !== routeId);
      await api.post('/transport-routes', updated);
      setTransportRoutes(updated);
      toast.success('Route deleted successfully');
    } catch (err) {
      toast.error('Failed to delete route');
    }
  };

  const handleDeleteDiscount = async (ruleId) => {
    if (!window.confirm('Are you sure you want to delete this discount rule?')) return;
    try {
      const updated = discountRules.filter(d => d.id !== ruleId);
      await api.post('/discount-rules', updated);
      setDiscountRules(updated);
      toast.success('Discount rule deleted successfully');
    } catch (err) {
      toast.error('Failed to delete rule');
    }
  };

  const handleSaveFieldSettings = async () => {
    setSavingFields(true);
    try {
      await api.post('/student-field-settings', fieldSettings);
      toast.success('Student field settings saved');
    } catch {
      toast.error('Failed to save field settings');
    } finally {
      setSavingFields(false);
    }
  };

  const handleSavePortalSettings = async () => {
    setSavingPortal(true);
    try {
      await api.post('/student-portal-settings', portalSettings);
      toast.success('Portal settings saved');
    } catch {
      toast.error('Failed to save portal settings');
    } finally {
      setSavingPortal(false);
    }
  };

  const handleSaveColumnSettings = async () => {
    setSavingColumns(true);
    try {
      await api.post('/student-column-settings', columnSettings);
      toast.success('Column settings saved');
    } catch {
      toast.error('Failed to save column settings');
    } finally {
      setSavingColumns(false);
    }
  };

  const handleColDrop = (toIdx) => {
    if (colDragIdx === null || colDragIdx === toIdx) return;
    const next = [...columnSettings];
    const [moved] = next.splice(colDragIdx, 1);
    next.splice(toIdx, 0, moved);
    setColumnSettings(next);
    setColDragIdx(null);
    setColDragOverIdx(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="w-full space-y-8 animate-in fade-in duration-300">
      {/* <div>
        <h2 className="font-geom font-bold text-3xl text-slate-800 tracking-tight">School Configurations</h2>
        <p className="text-sm text-slate-500 mt-1">Manage school profile, sessions, components, transport routes, and sibling discount waiver parameters.</p>
      </div> */}

      {/* Tabs list selector styling (Fintech style) */}
      <div className="flex flex-wrap gap-2 p-1.5 bg-slate-100/60 rounded-2xl border border-slate-200/30 max-w-fit">
        <button
          onClick={() => setActiveTab('general')}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs font-bold transition-all uppercase tracking-wide ${
            activeTab === 'general' ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50'
          }`}
        >
          <School className="w-4 h-4" />
          General Info & Sessions
        </button>
        <button
          onClick={() => setActiveTab('components')}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs font-bold transition-all uppercase tracking-wide ${
            activeTab === 'components' ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50'
          }`}
        >
          <Sliders className="w-4 h-4" />
          Fee Components
        </button>
        <button
          onClick={() => setActiveTab('routes')}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs font-bold transition-all uppercase tracking-wide ${
            activeTab === 'routes' ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50'
          }`}
        >
          <Truck className="w-4 h-4" />
          Transport Routes
        </button>
        {/* <button
          onClick={() => setActiveTab('discounts')}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs font-bold transition-all uppercase tracking-wide ${
            activeTab === 'discounts' ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50'
          }`}
        >
          <Percent className="w-4 h-4" />
          Discount Rules
        </button> */}
        <button
          onClick={() => setActiveTab('workflows')}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs font-bold transition-all uppercase tracking-wide ${
            activeTab === 'workflows' ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50'
          }`}
        >
          <GitBranch className="w-4 h-4" />
          Fee Workflows
        </button>
        <button
          onClick={() => setActiveTab('student-fields')}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs font-bold transition-all uppercase tracking-wide ${
            activeTab === 'student-fields' ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50'
          }`}
        >
          <Settings className="w-4 h-4" />
          Student Fields
        </button>
      </div>

      <div className="grid grid-cols-1 gap-8">
        {/* Tab 1: General Info & Sessions */}
        {activeTab === 'general' && (
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-white rounded-2xl border border-slate-200/40 shadow-sm overflow-hidden p-6 space-y-6">
                <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
                  <School className="w-5 h-5 text-indigo-600" />
                  <h3 className="font-geom font-bold text-slate-800 text-lg">School Profile</h3>
                </div>
                <form onSubmit={handleSchoolUpdate} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-400 uppercase mb-1.5">School Name</label>
                      <input 
                        type="text" 
                        value={school?.name || ''} 
                        onChange={(e) => setSchool({...school, name: e.target.value})}
                        className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 transition-all outline-none text-sm text-slate-800 font-medium"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-400 uppercase mb-1.5">School Code</label>
                      <input 
                        type="text" 
                        value={school?.schoolCode || ''} 
                        className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-slate-100 text-slate-500 cursor-not-allowed outline-none text-sm font-bold"
                        disabled
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-400 uppercase mb-1.5">Address</label>
                    <textarea 
                      value={school?.address || ''} 
                      onChange={(e) => setSchool({...school, address: e.target.value})}
                      className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 transition-all outline-none text-sm text-slate-800 font-medium"
                      rows="3"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-400 uppercase mb-1.5">Admin Contact</label>
                    <input 
                      type="text" 
                      value={school?.adminContact || ''} 
                      onChange={(e) => setSchool({...school, adminContact: e.target.value})}
                      className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 transition-all outline-none text-sm text-slate-800 font-medium"
                      required
                    />
                  </div>
                  <div className="border-t border-slate-100 pt-4 space-y-4">
                    <label className="block text-xs font-bold text-slate-400 uppercase tracking-wide">School Logo</label>

                    {/* Logo preview + upload area */}
                    <div className="flex items-start gap-4">
                      {/* Preview box */}
                      <div className="w-24 h-24 rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50 flex items-center justify-center shrink-0 overflow-hidden">
                        {school?.logoUrl
                          ? <img src={school.logoUrl} alt="School logo" className="w-full h-full object-contain p-1" />
                          : <ImageIcon className="w-8 h-8 text-slate-300" />
                        }
                      </div>

                      <div className="flex-1 space-y-2.5">
                        {/* Upload button */}
                        <div>
                          <input
                            ref={logoFileRef}
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              setLogoUploading(true);
                              try {
                                const form = new FormData();
                                form.append('file', file);
                                if (school?.logoUrl) form.append('oldUrl', school.logoUrl);
                                const res = await api.post('/upload/logo', form, {
                                  headers: { 'Content-Type': 'multipart/form-data' },
                                });
                                setSchool({ ...school, logoUrl: res.data.url });
                                toast.success('Logo uploaded');
                              } catch {
                                toast.error('Upload failed — check AWS credentials in .env');
                              } finally {
                                setLogoUploading(false);
                                e.target.value = '';
                              }
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => logoFileRef.current?.click()}
                            disabled={logoUploading}
                            className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-sm font-semibold text-slate-700 transition-colors disabled:opacity-60"
                          >
                            {logoUploading
                              ? <><Loader2 className="w-4 h-4 animate-spin text-indigo-500" /> Uploading…</>
                              : <><Upload className="w-4 h-4 text-indigo-500" /> Upload from device</>
                            }
                          </button>
                          <p className="text-[10px] text-slate-400 mt-1">PNG, JPG, WEBP · max 5 MB</p>
                        </div>

                        {/* Manual URL fallback */}
                        <div className="flex items-center gap-2">
                          <Link className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                          <input
                            type="text"
                            value={school?.logoUrl || ''}
                            onChange={(e) => setSchool({ ...school, logoUrl: e.target.value })}
                            className="flex-1 px-3 py-2 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 transition-all outline-none text-xs text-slate-600 font-medium"
                            placeholder="Or paste an image URL directly…"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Receipt placement */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-bold text-slate-400 uppercase mb-1.5">Receipt Logo Placement</label>
                        <select
                          value={school?.logoPlacement || 'center'}
                          onChange={(e) => setSchool({ ...school, logoPlacement: e.target.value })}
                          className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 transition-all outline-none text-sm text-slate-800 font-bold cursor-pointer"
                        >
                          <option value="left">Left Aligned</option>
                          <option value="center">Centered</option>
                          <option value="right">Right Aligned</option>
                        </select>
                      </div>
                    </div>

                  </div>
                  <div className="pt-2">
                    <button 
                      type="submit" 
                      disabled={savingSchool}
                      className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-3 rounded-full font-bold text-xs uppercase tracking-wide shadow-sm transition-all active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2 cursor-pointer"
                    >
                      <Save className="w-4 h-4" />
                      {savingSchool ? 'Saving Changes...' : 'Save Profile Changes'}
                    </button>
                  </div>
                </form>
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-white rounded-2xl border border-slate-200/40 shadow-sm p-6 space-y-6">
                <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
                  <Settings className="w-5 h-5 text-indigo-600" />
                  <h3 className="font-geom font-bold text-slate-800 text-lg">Academic Years</h3>
                </div>
                <form onSubmit={handleAddSession} className="flex gap-2">
                  <input 
                    type="text" 
                    placeholder="e.g. 2026-2027"
                    value={newSession}
                    onChange={(e) => setNewSession(e.target.value)}
                    className="flex-1 px-4 py-2.5 text-xs rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 outline-none transition-all"
                    required
                  />
                  <button type="submit" className="p-2.5 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-colors flex items-center justify-center">
                    <Plus className="w-5 h-5" />
                  </button>
                </form>
                
                <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
                  {sessions.map(session => (
                    <div key={session.id} className="flex items-center justify-between p-3 bg-slate-50/50 rounded-xl border border-slate-100 group">
                      <span className="font-bold text-slate-700 text-xs">{session.sessionYear}</span>
                      <button 
                        onClick={() => handleDeleteSession(session.id)}
                        className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-all"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tab 2: Fee Components */}
        {activeTab === 'components' && (() => {
          // ── Derived data ──────────────────────────────────────────────
          const studentsByClass = students.reduce((acc, s) => {
            acc[s.classId] = (acc[s.classId] || 0) + 1;
            return acc;
          }, {});
          const globalComps  = feeComponents.filter(c => c.classId === 'all');
          const classComps   = feeComponents.filter(c => c.classId !== 'all');
          const totalStudents = students.length;

          const monthlyRevenue = feeComponents.reduce((sum, c) => {
            const n = c.classId === 'all' ? totalStudents : (studentsByClass[c.classId] || 0);
            const m = c.frequency === 'monthly' ? c.amount
                    : c.frequency === 'quarterly' ? c.amount / 3
                    : c.frequency === 'annual'    ? c.amount / 12 : 0;
            return sum + m * n;
          }, 0);

          // Group class components by classId
          const compsByClass = {};
          classComps.forEach(c => { (compsByClass[c.classId] = compsByClass[c.classId] || []).push(c); });

          // Filtered list for search / filters
          const filtered = feeComponents.filter(c => {
            const q = compSearch.toLowerCase();
            if (q && !c.name.toLowerCase().includes(q)) return false;
            if (compFreqFilter !== 'all' && c.frequency !== compFreqFilter) return false;
            if (compScopeFilter === 'global' && c.classId !== 'all') return false;
            if (compScopeFilter === 'class'  && c.classId === 'all') return false;
            if (compClassFilter !== 'all' && c.classId !== 'all' && c.classId !== compClassFilter) return false;
            if (compSubTab === 'global' && c.classId !== 'all') return false;
            if (compSubTab === 'classwise' && c.classId === 'all') return false;
            return true;
          });

          // Stats card helper
          const StatCard = ({ icon: Icon, iconBg, iconColor, label, value, sub }) => (
            <div className="flex items-center gap-3 bg-white border border-slate-200/60 rounded-2xl p-4 shadow-sm min-w-0">
              <div className={`w-10 h-10 rounded-xl ${iconBg} flex items-center justify-center shrink-0`}>
                <Icon className={`w-5 h-5 ${iconColor}`} />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] text-slate-400 font-semibold uppercase tracking-wide truncate">{label}</p>
                <p className="text-xl font-black text-slate-800 leading-tight">{value}</p>
                <p className="text-[10px] text-slate-400 truncate">{sub}</p>
              </div>
            </div>
          );

          // Right panel selected-component details
          const sc = selectedComp ? feeComponents.find(c => c.id === selectedComp) : null;
          const scStudents = sc ? (sc.classId === 'all' ? totalStudents : (studentsByClass[sc.classId] || 0)) : 0;
          const scopeLabel = sc ? (sc.classId === 'all' ? 'Global (All Classes)' : `Class: ${classes.find(c => c.id === sc.classId)?.className || '—'}`) : '';
          const usedInClasses = sc
            ? (sc.classId === 'all' ? classes : classes.filter(c => c.id === sc.classId))
            : [];

          const toggleClass = (cid) => {
            setExpandedClasses(prev => {
              const next = new Set(prev);
              next.has(cid) ? next.delete(cid) : next.add(cid);
              return next;
            });
          };

          return (
            <div className="space-y-4">
              {/* Header */}
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-bold text-xl text-slate-800">Fee Components Directory</h3>
                  <p className="text-xs text-slate-400 mt-0.5">Manage your fee components globally and by class</p>
                </div>
                <button
                  onClick={() => { setConfigModalType('component'); setShowConfigModal(true); }}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full px-5 py-2.5 text-xs font-bold shadow-sm transition-all flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" /> Add Component
                </button>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
                <StatCard icon={Layers}       iconBg="bg-indigo-50"  iconColor="text-indigo-600" label="Total Components"   value={feeComponents.length}  sub="All components" />
                <StatCard icon={Globe}        iconBg="bg-purple-50"  iconColor="text-purple-600" label="Global Components"  value={globalComps.length}    sub="Applicable to all classes" />
                <StatCard icon={BookOpen}     iconBg="bg-blue-50"    iconColor="text-blue-600"   label="Class Specific"     value={classComps.length}     sub="Applicable to specific classes" />
                <StatCard icon={Users}        iconBg="bg-amber-50"   iconColor="text-amber-600"  label="Students Affected"  value={totalStudents.toLocaleString()} sub="Total students" />
                <StatCard icon={DollarSign}   iconBg="bg-emerald-50" iconColor="text-emerald-600" label="Monthly Revenue"  value={`₹${Math.round(monthlyRevenue).toLocaleString('en-IN')}`} sub="Estimated monthly" />
              </div>

              {/* Sub-tabs */}
              <div className="flex gap-0 border-b border-slate-200">
                {[['all','All Components'],['global','Global Components'],['classwise','Class Wise Components']].map(([v,l]) => (
                  <button key={v} onClick={() => setCompSubTab(v)}
                    className={`px-4 py-2.5 text-xs font-bold border-b-2 transition-all -mb-px ${compSubTab===v ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
                    {l}
                  </button>
                ))}
              </div>

              {/* Filter row */}
              <div className="flex flex-wrap items-center gap-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                  <input value={compSearch} onChange={e => setCompSearch(e.target.value)}
                    placeholder="Search component..."
                    className="pl-8 pr-3 py-2 text-xs rounded-xl border border-slate-200 bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400 outline-none w-44" />
                </div>
                <select value={compFreqFilter} onChange={e => setCompFreqFilter(e.target.value)}
                  className="px-3 py-2 text-xs rounded-xl border border-slate-200 bg-white outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400 font-semibold text-slate-600">
                  <option value="all">Frequency: All</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="annual">Annual</option>
                  <option value="one-time">One-Time</option>
                </select>
                <select value={compScopeFilter} onChange={e => setCompScopeFilter(e.target.value)}
                  className="px-3 py-2 text-xs rounded-xl border border-slate-200 bg-white outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400 font-semibold text-slate-600">
                  <option value="all">Scope: All</option>
                  <option value="global">Global</option>
                  <option value="class">Class Specific</option>
                </select>
                <select value={compClassFilter} onChange={e => setCompClassFilter(e.target.value)}
                  className="px-3 py-2 text-xs rounded-xl border border-slate-200 bg-white outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400 font-semibold text-slate-600">
                  <option value="all">Class: All Classes</option>
                  {classes.map(c => <option key={c.id} value={c.id}>{c.className}</option>)}
                </select>
                <button onClick={() => { setCompSearch(''); setCompFreqFilter('all'); setCompScopeFilter('all'); setCompClassFilter('all'); }}
                  className="flex items-center gap-1.5 px-3 py-2 text-xs rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-500 font-semibold transition-colors">
                  <RotateCcw className="w-3.5 h-3.5" /> Reset
                </button>
                <button onClick={() => {
                  const csv = ['Name,Amount,Frequency,Scope,Type',
                    ...feeComponents.map(c => `${c.name},${c.amount},${c.frequency},${c.classId==='all'?'Global':classes.find(x=>x.id===c.classId)?.className||c.classId},${c.type}`)
                  ].join('\n');
                  const a = document.createElement('a'); a.href = URL.createObjectURL(new Blob([csv],{type:'text/csv'}));
                  a.download = 'fee-components.csv'; a.click();
                }} className="ml-auto flex items-center gap-1.5 px-3 py-2 text-xs rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-500 font-semibold transition-colors">
                  <Download className="w-3.5 h-3.5" /> Export
                </button>
              </div>

              {/* Two-panel */}
              <div className="flex gap-4" style={{ minHeight: 480 }}>

                {/* Left tree */}
                <div className="w-64 shrink-0 bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden flex flex-col">
                  <div className="px-4 py-3 border-b border-slate-100">
                    <p className="text-xs font-black text-slate-500 uppercase tracking-wider">Component Structure</p>
                  </div>
                  <div className="flex-1 overflow-y-auto p-2 space-y-1">

                    {/* Global section */}
                    <button onClick={() => setTreeExpanded(t => ({...t, global: !t.global}))}
                      className="w-full flex items-center gap-2 px-2 py-2 rounded-xl hover:bg-slate-50 text-left transition-colors">
                      {treeExpanded.global ? <ChevronDown className="w-3.5 h-3.5 text-slate-400" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-400" />}
                      <Globe className="w-3.5 h-3.5 text-purple-500" />
                      <span className="text-xs font-bold text-slate-700">Global Components</span>
                      <span className="ml-auto text-[10px] bg-purple-50 text-purple-600 font-black px-1.5 py-0.5 rounded-full">{globalComps.length}</span>
                    </button>
                    {treeExpanded.global && filtered.filter(c => c.classId === 'all').map(c => (
                      <button key={c.id} onClick={() => setSelectedComp(c.id)}
                        className={`w-full flex items-center gap-2 pl-6 pr-2 py-1.5 rounded-xl text-left transition-colors text-xs ${selectedComp === c.id ? 'bg-indigo-50 text-indigo-700 font-semibold' : 'hover:bg-slate-50 text-slate-600'}`}>
                        <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${selectedComp===c.id?'bg-indigo-500':'bg-slate-300'}`} />
                        <span className="flex-1 truncate">{c.name}</span>
                        <span className="text-[9px] text-slate-400 shrink-0">All Classes</span>
                      </button>
                    ))}

                    {/* Class-wise section */}
                    <button onClick={() => setTreeExpanded(t => ({...t, classwise: !t.classwise}))}
                      className="w-full flex items-center gap-2 px-2 py-2 rounded-xl hover:bg-slate-50 text-left transition-colors mt-1">
                      {treeExpanded.classwise ? <ChevronDown className="w-3.5 h-3.5 text-slate-400" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-400" />}
                      <BookOpen className="w-3.5 h-3.5 text-blue-500" />
                      <span className="text-xs font-bold text-slate-700">Class Wise Components</span>
                      <span className="ml-auto text-[10px] bg-blue-50 text-blue-600 font-black px-1.5 py-0.5 rounded-full">{classComps.length}</span>
                    </button>
                    {treeExpanded.classwise && classes.filter(c => compsByClass[c.id]?.length > 0).map(cls => (
                      <div key={cls.id}>
                        <button onClick={() => toggleClass(cls.id)}
                          className="w-full flex items-center gap-2 pl-4 pr-2 py-1.5 rounded-xl hover:bg-slate-50 text-left transition-colors">
                          {expandedClasses.has(cls.id) ? <ChevronDown className="w-3 h-3 text-slate-400" /> : <ChevronRight className="w-3 h-3 text-slate-400" />}
                          <GraduationCap className="w-3 h-3 text-slate-400" />
                          <span className="text-xs font-semibold text-slate-600 flex-1">{cls.className}</span>
                          <span className="text-[9px] text-slate-400">{compsByClass[cls.id]?.length}</span>
                        </button>
                        {expandedClasses.has(cls.id) && (compsByClass[cls.id] || []).filter(c => {
                          const q = compSearch.toLowerCase();
                          return !q || c.name.toLowerCase().includes(q);
                        }).map(c => (
                          <button key={c.id} onClick={() => setSelectedComp(c.id)}
                            className={`w-full flex items-center gap-2 pl-9 pr-2 py-1.5 rounded-xl text-left transition-colors text-xs ${selectedComp===c.id?'bg-indigo-50 text-indigo-700 font-semibold':'hover:bg-slate-50 text-slate-600'}`}>
                            <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${selectedComp===c.id?'bg-indigo-500':'bg-slate-300'}`} />
                            <span className="flex-1 truncate">{c.name}</span>
                          </button>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Right detail panel */}
                <div className="flex-1 bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden flex flex-col">
                  {!sc ? (
                    <div className="flex-1 flex flex-col items-center justify-center text-center text-slate-300 p-8">
                      <Sliders className="w-14 h-14 mb-4 opacity-30" />
                      <p className="font-semibold text-slate-400">Select a component to view details</p>
                      <p className="text-xs text-slate-300 mt-1">Click any item in the component structure</p>
                    </div>
                  ) : (
                    <>
                      {/* Detail header */}
                      <div className="flex items-start gap-4 p-5 border-b border-slate-100">
                        <div className="w-12 h-12 rounded-2xl bg-indigo-100 flex items-center justify-center shrink-0">
                          <DollarSign className="w-6 h-6 text-indigo-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="font-black text-lg text-slate-800">{sc.name}</h4>
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${sc.classId==='all'?'bg-purple-50 text-purple-700 border-purple-200':'bg-blue-50 text-blue-700 border-blue-200'}`}>
                              {sc.classId === 'all' ? 'Global Component' : 'Class Specific'}
                            </span>
                          </div>
                          <p className="text-xs text-slate-400 mt-0.5">
                            {sc.classId==='all'?'Applicable to all classes.':scopeLabel} {sc.frequency.charAt(0).toUpperCase()+sc.frequency.slice(1)} {sc.type} fee component.
                          </p>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <button onClick={() => { setEditingComp({...sc}); setConfigModalType('edit-component'); setShowConfigModal(true); }}
                            className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 hover:bg-slate-50 rounded-lg text-xs font-semibold text-slate-600 transition-colors">
                            <Edit2 className="w-3.5 h-3.5" /> Edit
                          </button>
                          <button onClick={async () => {
                            const copy = { ...sc, id: `comp-${Date.now()}`, name: `${sc.name} (Copy)` };
                            try {
                              const updated = [...feeComponents, copy];
                              await api.post('/fee-components', updated);
                              setFeeComponents(updated);
                              toast.success('Component duplicated');
                            } catch { toast.error('Failed to duplicate'); }
                          }} className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 hover:bg-slate-50 rounded-lg text-xs font-semibold text-slate-600 transition-colors">
                            <Copy className="w-3.5 h-3.5" /> Duplicate
                          </button>
                          <button onClick={() => { handleDeleteComponent(sc.id); setSelectedComp(null); }}
                            className="flex items-center gap-1.5 px-3 py-1.5 border border-red-200 bg-red-50 hover:bg-red-100 rounded-lg text-xs font-semibold text-red-600 transition-colors">
                            <Trash2 className="w-3.5 h-3.5" /> Delete
                          </button>
                        </div>
                      </div>

                      {/* Metadata grid */}
                      <div className="grid grid-cols-3 md:grid-cols-6 gap-0 border-b border-slate-100">
                        {[
                          { label:'Frequency', value: sc.frequency.charAt(0).toUpperCase()+sc.frequency.slice(1) },
                          { label:'Scope', value: sc.classId==='all'?'Global (All Classes)':classes.find(c=>c.id===sc.classId)?.className||'—' },
                          { label:'Billing Priority', value: sc.type.charAt(0).toUpperCase()+sc.type.slice(1) },
                          { label:'Default Amount', value: `₹${sc.amount.toLocaleString('en-IN')}` },
                          { label:'Students Affected', value: scStudents.toLocaleString() },
                          { label:'Status', value: 'Active', isStatus: true },
                        ].map(({ label, value, isStatus }) => (
                          <div key={label} className="flex flex-col items-center justify-center py-4 px-3 border-r border-slate-100 last:border-r-0 text-center">
                            <p className="text-[9px] text-slate-400 uppercase tracking-wider font-bold mb-1">{label}</p>
                            {isStatus
                              ? <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
                                  <CheckCircle className="w-3 h-3" />{value}
                                </span>
                              : <p className="text-sm font-bold text-slate-800">{value}</p>
                            }
                          </div>
                        ))}
                      </div>

                      {/* Used In Classes table */}
                      <div className="flex-1 overflow-y-auto">
                        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-50">
                          <div>
                            <p className="text-sm font-bold text-slate-800">Used In Classes</p>
                            <p className="text-[11px] text-slate-400">This component is applied to the following classes</p>
                          </div>
                          <span className="text-xs text-slate-400 font-semibold">Total Classes: {usedInClasses.length}</span>
                        </div>
                        <table className="w-full text-left">
                          <thead>
                            <tr className="bg-slate-50/60 border-b border-slate-100">
                              <th className="px-5 py-2.5 text-[9px] font-black text-slate-400 uppercase tracking-widest">Class</th>
                              <th className="px-5 py-2.5 text-[9px] font-black text-slate-400 uppercase tracking-widest">Students</th>
                              <th className="px-5 py-2.5 text-[9px] font-black text-slate-400 uppercase tracking-widest">Amount (₹)</th>
                              <th className="px-5 py-2.5 text-[9px] font-black text-slate-400 uppercase tracking-widest">Frequency</th>
                              <th className="px-5 py-2.5 text-[9px] font-black text-slate-400 uppercase tracking-widest">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {usedInClasses.slice(0, 8).map(cls => (
                              <tr key={cls.id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors">
                                <td className="px-5 py-3 text-sm font-semibold text-slate-800">{cls.className}</td>
                                <td className="px-5 py-3 text-sm text-slate-600">{(studentsByClass[cls.id] || 0).toLocaleString()}</td>
                                <td className="px-5 py-3 text-sm font-semibold text-slate-800">{sc.amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                <td className="px-5 py-3 text-xs text-slate-500 capitalize">{sc.frequency}</td>
                                <td className="px-5 py-3">
                                  <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">Active</span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {usedInClasses.length > 8 && (
                          <p className="px-5 py-3 text-xs text-indigo-600 font-semibold cursor-pointer hover:underline">
                            View all {usedInClasses.length} classes
                          </p>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })()}

        {/* Tab 3: Transport Routes */}
        {activeTab === 'routes' && (
          <div className="bg-white rounded-2xl border border-slate-200/40 p-6 shadow-sm space-y-6">
            <div className="flex items-center justify-between pb-4 border-b border-slate-100">
              <div>
                <h3 className="font-geom font-bold text-slate-800 text-lg">Transport Fleet Routes</h3>
                <p className="text-xs text-slate-400">Manage pickup points and monthly transport pricing structures</p>
              </div>
              <button 
                onClick={() => { setConfigModalType('transport'); setShowConfigModal(true); }}
                className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full px-5 py-2.5 text-xs font-bold shadow-sm transition-all flex items-center gap-2 cursor-pointer"
              >
                <Plus className="w-4 h-4" />
                Add Fleet Route
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {transportRoutes.map(route => (
                <div key={route.id} className="p-4 bg-slate-50/40 rounded-2xl border border-slate-200/30 flex flex-col justify-between hover:border-slate-300 transition-all">
                  <div className="space-y-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="text-sm font-bold text-slate-800">{route.name}</h4>
                        <span className="text-[10px] text-slate-400 font-medium">Code: {route.routeCode}</span>
                      </div>
                      <span className="text-base font-geom font-bold text-indigo-600">₹{route.monthlyFee}/mo</span>
                    </div>
                    
                    <div className="space-y-1">
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">Pickup Stops</p>
                      <div className="flex flex-wrap gap-1.5">
                        {route.pickupPoints?.map((point, idx) => (
                          <span key={idx} className="px-2 py-0.5 bg-white border border-slate-200 text-slate-600 rounded text-[9px] font-semibold">
                            {point}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex justify-end pt-4 mt-4 border-t border-slate-100">
                    <button 
                      onClick={() => handleDeleteRoute(route.id)}
                      className="text-xs font-bold text-rose-500 hover:text-rose-700 flex items-center gap-1 cursor-pointer"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      Delete Route
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tab 4: Discount Rules */}
        {activeTab === 'discounts' && (
          <div className="bg-white rounded-2xl border border-slate-200/40 p-6 shadow-sm space-y-6">
            <div className="flex items-center justify-between pb-4 border-b border-slate-100">
              <div>
                <h3 className="font-geom font-bold text-slate-800 text-lg">Sibling Waiver Rules</h3>
                <p className="text-xs text-slate-400">Configure real-time family or merit discount allocation scripts</p>
              </div>
              <button 
                onClick={() => { setConfigModalType('discount'); setShowConfigModal(true); }}
                className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full px-5 py-2.5 text-xs font-bold shadow-sm transition-all flex items-center gap-2 cursor-pointer"
              >
                <Plus className="w-4 h-4" />
                Add Discount Waiver
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {discountRules.map(rule => (
                <div key={rule.id} className="p-4 bg-slate-50/40 rounded-2xl border border-slate-200/30 flex flex-col justify-between hover:border-slate-300 transition-all">
                  <div className="space-y-3">
                    <div className="flex items-start justify-between">
                      <h4 className="text-sm font-bold text-slate-800">{rule.name}</h4>
                      <span className="text-xs font-geom font-bold bg-green-50 text-green-700 px-2 py-0.5 rounded border border-green-200/40">
                        {rule.valueType === 'percentage' ? `${rule.value}% OFF` : `₹${rule.value} OFF`}
                      </span>
                    </div>
                    <p className="text-xs text-slate-400 leading-relaxed font-medium">
                      Condition Rule: {rule.condition}
                    </p>
                    <p className="text-[10px] text-slate-400 capitalize">
                      Applies to: {rule.target} component
                    </p>
                  </div>
                  
                  <div className="flex justify-end pt-4 mt-4 border-t border-slate-100">
                    <button 
                      onClick={() => handleDeleteDiscount(rule.id)}
                      className="text-xs font-bold text-rose-500 hover:text-rose-700 flex items-center gap-1 cursor-pointer"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      Remove Waiver
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {/* Tab 5: Fee Workflows */}
        {activeTab === 'workflows' && (
          <div className="bg-white rounded-2xl border border-slate-200/40 shadow-sm overflow-hidden" style={{ minHeight: 'calc(100vh - 280px)' }}>
            <DiscountWorkflowPage />
          </div>
        )}

        {activeTab === 'student-fields' && (
          <div className="grid lg:grid-cols-2 gap-6 items-start">
            <div className="bg-white rounded-2xl border border-slate-200/40 shadow-sm p-6 space-y-5">
              <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
                <Settings className="w-5 h-5 text-indigo-600" />
                <div>
                  <h3 className="font-geom font-bold text-slate-800 text-base">Mandatory Student Fields</h3>
                  <p className="text-xs text-slate-400 mt-0.5">Toggle which fields are required when adding or importing students</p>
                </div>
              </div>

              {/* Always-required notice */}
              <div className="p-3 bg-slate-50 rounded-xl border border-slate-200/50">
                <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2">Always Required</p>
                <div className="flex flex-wrap gap-2">
                  {['Student Name', 'Class', 'Section'].map(f => (
                    <span key={f} className="px-2.5 py-1 bg-indigo-50 text-indigo-700 text-[11px] font-semibold rounded-full border border-indigo-100">
                      {f}
                    </span>
                  ))}
                </div>
              </div>

              {/* Toggleable fields */}
              <div className="space-y-3">
                {[
                  { key: 'rollNumber',      label: 'Roll Number',      hint: 'Class roll number / serial number' },
                  { key: 'admissionNumber', label: 'Admission Number',  hint: 'Unique admission / enrolment number' },
                  { key: 'dateOfBirth',     label: 'Date of Birth',     hint: 'Student date of birth (YYYY-MM-DD)' },
                  { key: 'fatherName',   label: 'Father Name',   hint: "Father's full name" },
                  { key: 'fatherPhone',  label: 'Father Phone',  hint: "Father's 10-digit contact number" },
                  { key: 'motherName',   label: 'Mother Name',   hint: "Mother's full name" },
                  { key: 'motherPhone',  label: 'Mother Phone',  hint: "Mother's 10-digit contact number" },
                  { key: 'aadharNumber', label: 'Aadhar Number', hint: "Student's 12-digit Aadhar number" },
                  { key: 'address',      label: 'Address',       hint: 'Residential address' },
                  { key: 'familyId',     label: 'Family ID',     hint: 'Groups siblings for discount tracking' },
                ].map(({ key, label, hint }) => (
                  <div key={key} className="flex items-center justify-between p-4 rounded-xl border border-slate-200/60 bg-slate-50/40 hover:bg-slate-50 transition-colors">
                    <div>
                      <p className="text-sm font-semibold text-slate-700">{label}</p>
                      <p className="text-[11px] text-slate-400 mt-0.5">{hint}</p>
                    </div>
                    <label className="flex items-center gap-2 cursor-pointer select-none shrink-0">
                      <span className={`text-xs font-semibold ${fieldSettings[key] ? 'text-indigo-600' : 'text-slate-400'}`}>
                        {fieldSettings[key] ? 'Required' : 'Optional'}
                      </span>
                      <div className="relative">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={!!fieldSettings[key]}
                          onChange={e => setFieldSettings(s => ({ ...s, [key]: e.target.checked }))}
                        />
                        <div className="w-10 h-6 bg-slate-200 peer-checked:bg-indigo-500 rounded-full transition-colors" />
                        <div className="absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform peer-checked:translate-x-4" />
                      </div>
                    </label>
                  </div>
                ))}
              </div>

              <button
                onClick={handleSaveFieldSettings}
                disabled={savingFields}
                className="w-full flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white rounded-full py-3 font-semibold text-sm transition-all shadow-sm"
              >
                {savingFields ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {savingFields ? 'Saving…' : 'Save Field Settings'}
              </button>
            </div>

            {/* ── Right column: Portal + Column Visibility ── */}
            <div className="space-y-6">

            {/* Student Portal Card */}
            <div className="bg-white rounded-2xl border border-slate-200/40 shadow-sm p-6 space-y-5">
              <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
                <KeyRound className="w-5 h-5 text-indigo-600" />
                <div>
                  <h3 className="font-geom font-bold text-slate-800 text-base">Student Login Credentials</h3>
                  <p className="text-xs text-slate-400 mt-0.5">Control whether student accounts are created in the system</p>
                </div>
              </div>

              <div className="flex items-center justify-between p-4 rounded-xl border border-slate-200/60 bg-slate-50/40">
                <div>
                  <p className="text-sm font-semibold text-slate-700">Create credentials on admission</p>
                  <p className="text-[11px] text-slate-400 mt-0.5">
                    When enabled, a login account is automatically created for every student added manually or via CSV import.
                    Turn off if your school does not use the student portal.
                  </p>
                </div>
                <label className="flex items-center gap-2 cursor-pointer select-none shrink-0 ml-4">
                  <span className={`text-xs font-semibold ${portalSettings.createCredentials ? 'text-indigo-600' : 'text-slate-400'}`}>
                    {portalSettings.createCredentials ? 'Enabled' : 'Disabled'}
                  </span>
                  <div className="relative">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={!!portalSettings.createCredentials}
                      onChange={e => setPortalSettings(s => ({ ...s, createCredentials: e.target.checked }))}
                    />
                    <div className="w-10 h-6 bg-slate-200 peer-checked:bg-indigo-500 rounded-full transition-colors" />
                    <div className="absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform peer-checked:translate-x-4" />
                  </div>
                </label>
              </div>

              {!portalSettings.createCredentials && (
                <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200/60 rounded-xl">
                  <span className="text-amber-500 text-sm shrink-0 mt-0.5">⚠</span>
                  <p className="text-xs text-amber-700 font-medium">
                    No login accounts will be created for new students. Existing accounts are not affected.
                  </p>
                </div>
              )}

              <button
                onClick={handleSavePortalSettings}
                disabled={savingPortal}
                className="w-full flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white rounded-full py-3 font-semibold text-sm transition-all shadow-sm"
              >
                {savingPortal ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {savingPortal ? 'Saving…' : 'Save Portal Settings'}
              </button>
            </div>

            {/* Column Visibility Card */}
            <div className="bg-white rounded-2xl border border-slate-200/40 shadow-sm p-6 space-y-5">
              <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
                <Columns className="w-5 h-5 text-indigo-600" />
                <div>
                  <h3 className="font-geom font-bold text-slate-800 text-base">Table Column Visibility & Order</h3>
                  <p className="text-xs text-slate-400 mt-0.5">Choose which columns appear in the Students table and drag to reorder them</p>
                </div>
              </div>

              <p className="text-[11px] text-slate-400 flex items-center gap-1.5">
                <GripVertical className="w-3.5 h-3.5" /> Drag rows to reorder · Toggle to show/hide
              </p>

              <div className="space-y-2">
                {columnSettings.map((col, idx) => {
                  const meta = COLUMN_META[col.key];
                  const isDragging = colDragIdx === idx;
                  const isOver    = colDragOverIdx === idx;
                  return (
                    <div
                      key={col.key}
                      draggable={!meta?.alwaysVisible}
                      onDragStart={() => setColDragIdx(idx)}
                      onDragOver={e => { e.preventDefault(); setColDragOverIdx(idx); }}
                      onDrop={() => handleColDrop(idx)}
                      onDragEnd={() => { setColDragIdx(null); setColDragOverIdx(null); }}
                      className={`flex items-center gap-3 p-3.5 rounded-xl border transition-all select-none
                        ${meta?.alwaysVisible ? 'cursor-default opacity-80' : 'cursor-grab active:cursor-grabbing'}
                        ${isDragging ? 'opacity-40 scale-95' : ''}
                        ${isOver && !isDragging ? 'border-indigo-400 bg-indigo-50/60 shadow-sm' : 'border-slate-200/60 bg-slate-50/40 hover:bg-slate-50'}`}
                    >
                      <GripVertical className={`w-4 h-4 shrink-0 ${meta?.alwaysVisible ? 'text-slate-200' : 'text-slate-300'}`} />
                      <span className="flex-1 text-sm font-semibold text-slate-700">{meta?.label}</span>
                      {meta?.alwaysVisible ? (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-400 border border-slate-200/60 uppercase tracking-wide">
                          Always On
                        </span>
                      ) : (
                        <label className="flex items-center gap-2 cursor-pointer shrink-0">
                          <span className={`text-xs font-semibold ${col.visible ? 'text-indigo-600' : 'text-slate-400'}`}>
                            {col.visible ? 'Visible' : 'Hidden'}
                          </span>
                          <div className="relative">
                            <input
                              type="checkbox"
                              className="sr-only peer"
                              checked={!!col.visible}
                              onChange={e => setColumnSettings(prev =>
                                prev.map((c, i) => i === idx ? { ...c, visible: e.target.checked } : c)
                              )}
                            />
                            <div className="w-9 h-5 bg-slate-200 peer-checked:bg-indigo-500 rounded-full transition-colors" />
                            <div className="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform peer-checked:translate-x-4" />
                          </div>
                        </label>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="flex items-center gap-3 pt-1">
                <button
                  type="button"
                  onClick={() => setColumnSettings(DEFAULT_COLUMN_SETTINGS)}
                  className="flex items-center gap-1.5 px-4 py-2.5 border border-slate-200 hover:bg-slate-50 rounded-full text-xs font-semibold text-slate-500 transition-colors"
                >
                  <RotateCcw className="w-3.5 h-3.5" /> Reset to Default
                </button>
                <button
                  onClick={handleSaveColumnSettings}
                  disabled={savingColumns}
                  className="flex-1 flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white rounded-full py-2.5 font-semibold text-sm transition-all shadow-sm"
                >
                  {savingColumns ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {savingColumns ? 'Saving…' : 'Save Column Settings'}
                </button>
              </div>
            </div>

            </div>{/* end right column */}
          </div>
        )}
      </div>

      {/* Slide Modal logic from FeesPage */}
      <RightSlideModal
        isOpen={showConfigModal}
        onClose={() => { setShowConfigModal(false); setEditingComp(null); }}
        title={
          configModalType === 'component'      ? 'Add New Fee Component' :
          configModalType === 'edit-component' ? 'Edit Fee Component' :
          configModalType === 'transport'      ? 'Add New Transport Route' :
          'Add New Discount Waiver Rule'
        }
      >
        {configModalType === 'component' && (
          <form 
            onSubmit={async (e) => {
              e.preventDefault();
              const newComp = {
                id: `comp-${Date.now()}`,
                name: compForm.name,
                amount: parseFloat(compForm.amount),
                frequency: compForm.frequency,
                type: compForm.type,
                classId: compForm.classId || 'all'
              };
              try {
                const updatedComps = [...feeComponents, newComp];
                await api.post('/fee-components', updatedComps);
                setFeeComponents(updatedComps);
                setCompForm({ name: '', amount: '', frequency: 'monthly', type: 'mandatory', classId: 'all' });
                setShowConfigModal(false);
                toast.success('Fee component persisted to MongoDB successfully!');
              } catch (err) {
                toast.error('Failed to save component configuration to database');
              }
            }}
            className="space-y-4"
          >
            <div>
              <label className="block text-xs font-bold text-slate-400 uppercase mb-1.5">Component Name</label>
              <input 
                type="text" 
                value={compForm.name} 
                onChange={(e) => setCompForm({...compForm, name: e.target.value})}
                className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 outline-none text-sm"
                placeholder="e.g. Sports Fee" 
                required 
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase mb-1.5">Amount (₹)</label>
                <input 
                  type="number" 
                  value={compForm.amount} 
                  onChange={(e) => setCompForm({...compForm, amount: e.target.value})}
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 outline-none text-sm"
                  placeholder="e.g. 500" 
                  required 
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase mb-1.5">Billing Type</label>
                <select 
                  value={compForm.type} 
                  onChange={(e) => setCompForm({...compForm, type: e.target.value})}
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 outline-none text-sm font-semibold text-slate-700 cursor-pointer"
                >
                  <option value="mandatory">Mandatory (All Students)</option>
                  <option value="optional">Optional (Configurable)</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase mb-1.5">Frequencies</label>
                <select 
                  value={compForm.frequency} 
                  onChange={(e) => setCompForm({...compForm, frequency: e.target.value})}
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 outline-none text-sm font-semibold text-slate-700 cursor-pointer"
                >
                  <option value="monthly">Monthly Billings</option>
                  <option value="quarterly">Quarterly Billings</option>
                  <option value="annual">Annual Billings</option>
                  <option value="one-time">One-Time Dues</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase mb-1.5">Target Class Scope</label>
                <select 
                  value={compForm.classId} 
                  onChange={(e) => setCompForm({...compForm, classId: e.target.value})}
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 outline-none text-sm font-semibold text-slate-700 cursor-pointer"
                >
                  <option value="all">All Classes (Global)</option>
                  {classes.map(c => <option key={c.id} value={c.id}>Class: {c.className}</option>)}
                </select>
              </div>
            </div>
            <button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700 text-white rounded-full py-3 font-semibold shadow-sm transition-all mt-4 cursor-pointer">
              Create Component
            </button>
          </form>
        )}

        {configModalType === 'edit-component' && editingComp && (
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              const updated = feeComponents.map(c => c.id === editingComp.id ? { ...editingComp } : c);
              try {
                await api.post('/fee-components', updated);
                setFeeComponents(updated);
                setShowConfigModal(false);
                setEditingComp(null);
                toast.success('Component updated');
              } catch { toast.error('Failed to update component'); }
            }}
            className="space-y-4"
          >
            <div>
              <label className="block text-xs font-bold text-slate-400 uppercase mb-1.5">Component Name</label>
              <input type="text" value={editingComp.name} onChange={e => setEditingComp({...editingComp, name: e.target.value})}
                className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 outline-none text-sm" required />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase mb-1.5">Amount (₹)</label>
                <input type="number" value={editingComp.amount} onChange={e => setEditingComp({...editingComp, amount: parseFloat(e.target.value)||0})}
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 outline-none text-sm" required />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase mb-1.5">Billing Type</label>
                <select value={editingComp.type} onChange={e => setEditingComp({...editingComp, type: e.target.value})}
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 outline-none text-sm font-semibold text-slate-700">
                  <option value="mandatory">Mandatory</option>
                  <option value="optional">Optional</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase mb-1.5">Frequency</label>
                <select value={editingComp.frequency} onChange={e => setEditingComp({...editingComp, frequency: e.target.value})}
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 outline-none text-sm font-semibold text-slate-700">
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="annual">Annual</option>
                  <option value="one-time">One-Time</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase mb-1.5">Target Class</label>
                <select value={editingComp.classId} onChange={e => setEditingComp({...editingComp, classId: e.target.value})}
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 outline-none text-sm font-semibold text-slate-700">
                  <option value="all">All Classes (Global)</option>
                  {classes.map(c => <option key={c.id} value={c.id}>{c.className}</option>)}
                </select>
              </div>
            </div>
            <button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700 text-white rounded-full py-3 font-semibold shadow-sm transition-all mt-4">
              Save Changes
            </button>
          </form>
        )}

        {configModalType === 'transport' && (
          <form 
            onSubmit={async (e) => {
              e.preventDefault();
              const newRoute = {
                id: `route-${Date.now()}`,
                name: routeForm.name,
                routeCode: routeForm.routeCode,
                monthlyFee: parseFloat(routeForm.monthlyFee),
                pickupPoints: routeForm.pickupPointInput.split(',').map(p => p.trim())
              };
              try {
                const updatedRoutes = [...transportRoutes, newRoute];
                await api.post('/transport-routes', updatedRoutes);
                setTransportRoutes(updatedRoutes);
                setRouteForm({ name: '', routeCode: '', monthlyFee: '', pickupPointInput: '' });
                setShowConfigModal(false);
                toast.success('Transport route persisted successfully!');
              } catch (err) {
                toast.error('Failed to save transport route configuration');
              }
            }}
            className="space-y-4"
          >
            <div>
              <label className="block text-xs font-bold text-slate-400 uppercase mb-1.5">Route Name</label>
              <input 
                type="text" 
                value={routeForm.name} 
                onChange={(e) => setRouteForm({...routeForm, name: e.target.value})}
                className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 outline-none text-sm"
                placeholder="e.g. Route D (West Sector)" 
                required 
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase mb-1.5">Route Code</label>
                <input 
                  type="text" 
                  value={routeForm.routeCode} 
                  onChange={(e) => setRouteForm({...routeForm, routeCode: e.target.value})}
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 outline-none text-sm uppercase"
                  placeholder="e.g. R-WEST" 
                  required 
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase mb-1.5">Monthly Charge (₹)</label>
                <input 
                  type="number" 
                  value={routeForm.monthlyFee} 
                  onChange={(e) => setRouteForm({...routeForm, monthlyFee: e.target.value})}
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 outline-none text-sm"
                  placeholder="e.g. 1500" 
                  required 
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-400 uppercase mb-1.5">Pickup Points (comma separated)</label>
              <textarea 
                value={routeForm.pickupPointInput} 
                onChange={(e) => setRouteForm({...routeForm, pickupPointInput: e.target.value})}
                className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 outline-none text-sm"
                rows="2"
                placeholder="Metro Station, Sector Gate, Crossroad junction"
                required 
              />
            </div>
            <button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700 text-white rounded-full py-3 font-semibold shadow-sm transition-all mt-4 cursor-pointer">
              Create Route
            </button>
          </form>
        )}

        {configModalType === 'discount' && (
          <form 
            onSubmit={async (e) => {
              e.preventDefault();
              const newRule = {
                id: `rule-${Date.now()}`,
                name: discountForm.name,
                target: discountForm.target,
                value: parseFloat(discountForm.value),
                valueType: discountForm.valueType,
                condition: discountForm.condition
              };
              try {
                const updatedRules = [...discountRules, newRule];
                await api.post('/discount-rules', updatedRules);
                setDiscountRules(updatedRules);
                setDiscountForm({ name: '', target: 'tuition', value: '', valueType: 'percentage', condition: '' });
                setShowConfigModal(false);
                toast.success('Discount waiver rule configured in database!');
              } catch (err) {
                toast.error('Failed to configure discount rule');
              }
            }}
            className="space-y-4"
          >
            <div>
              <label className="block text-xs font-bold text-slate-400 uppercase mb-1.5">Discount Name</label>
              <input 
                type="text" 
                value={discountForm.name} 
                onChange={(e) => setDiscountForm({...discountForm, name: e.target.value})}
                className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 outline-none text-sm"
                placeholder="e.g. Staff Benefit Waiver" 
                required 
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase mb-1.5">Discount Value</label>
                <input 
                  type="number" 
                  value={discountForm.value} 
                  onChange={(e) => setDiscountForm({...discountForm, value: e.target.value})}
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 outline-none text-sm"
                  placeholder="e.g. 50" 
                  required 
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase mb-1.5">Value Type</label>
                <select 
                  value={discountForm.valueType} 
                  onChange={(e) => setDiscountForm({...discountForm, valueType: e.target.value})}
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 outline-none text-sm font-semibold text-slate-700 cursor-pointer"
                >
                  <option value="percentage">Percentage (%) Discount</option>
                  <option value="fixed">Fixed Flat Waiver (₹)</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase mb-1.5">Target Component</label>
                <select 
                  value={discountForm.target} 
                  onChange={(e) => setDiscountForm({...discountForm, target: e.target.value})}
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 outline-none text-sm font-semibold text-slate-700 cursor-pointer"
                >
                  <option value="tuition">Tuition Fee Only</option>
                  <option value="transport">Transport Fee Only</option>
                  <option value="all">Overall Dues Total</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase mb-1.5">Condition Rule</label>
                <input 
                  type="text" 
                  value={discountForm.condition} 
                  onChange={(e) => setDiscountForm({...discountForm, condition: e.target.value})}
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 outline-none text-sm"
                  placeholder="e.g. Applied manually to second child" 
                  required 
                />
              </div>
            </div>
            <button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700 text-white rounded-full py-3 font-semibold shadow-sm transition-all mt-4 cursor-pointer">
              Configure Rule
            </button>
          </form>
        )}
      </RightSlideModal>
    </div>
  );
}
